import { useCallback, useState } from 'react';
import { downloadFile } from '../../components/utils';
import { FileGenerationResponse } from './useFileGeneration';

const BROWSER_DOWNLOAD_DELAY_BYPASS = 300;

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export interface UseFileDownloaderParams<TResponse extends FileGenerationResponse = FileGenerationResponse> {
    downloadUrl: string;
    onFailure?: (error: unknown) => void;
    onSuccess?: () => void;
}

export interface UseFileDownloaderReturn {
    downloadFiles: (responseIds: string | string[]) => Promise<void>;
    downloading: boolean;
    error: unknown;
}

export const useFileDownloader = ({
    downloadUrl,
    onSuccess,
    onFailure
}: UseFileDownloaderParams): UseFileDownloaderReturn => {
    const [downloading, setDownloading] = useState(false);
    const [error, setError] = useState<unknown | undefined>();

    const downloadFiles = useCallback(
        async (responseIds: string | string[]) => {
            setDownloading(true);
            setError(undefined);
            try {
                if (typeof responseIds === 'string') {
                    downloadFile(`${downloadUrl}/${responseIds}`);
                    onSuccess?.();
                } else if (Array.isArray(responseIds)) {
                    for (const id of responseIds) {
                        downloadFile(`${downloadUrl}/${id}`);
                        await delay(BROWSER_DOWNLOAD_DELAY_BYPASS);
                    }
                    onSuccess?.();
                }
            } catch (err) {
                setError(err);
                onFailure?.(error);
            } finally {
                setDownloading(false);
            }
        },
        [downloadUrl, onSuccess, onFailure]
    );

    return { downloadFiles, downloading, error };
};
